import { areMetricAndCategoryConsistent } from '../areMetricAndCategoryConsistent/areMetricAndCategoryConsistent';
import { ArticleSearchCategory } from '#lib/websites/news/constants/categories.js';
import { METRICS } from '#lib/websites/news/constants/dashboard.js';
import {
	HENI_TAG_PARAM,
	HENI_TAG_PARAM_VALUE,
} from '#lib/websites/news/constants/heni-tag.js';
import { ArticleSearchStatus } from '#lib/websites/news/constants/status.js';
import type { ReadonlyURLSearchParams } from '$app/state';
import { isQueryParamValid } from '$global/utils/isQueryParamValid/isQueryParamValid';

type Param = string | string[] | undefined;

export interface SearchQuery {
	geotag: Param;
	phrase: Param;
	artist: Param;
	globalCategory: Param;
	category: Param;
	daterange: Param;
	metric: Param;
	status: Param;
	startDate: Param;
	endDate: Param;
	heni: Param;
}

export const getSearchVariables = (query: ReadonlyURLSearchParams) => {
	const phraseParam = query?.get('phrase');
	const artistParam = query?.get('artist');
	const geotagParam = query?.get('geotag');
	const categoryParam = query?.get('category');
	const daterangeParam = query?.get('daterange');
	const metricParam = query?.get('metric');
	const statusParam = query?.get('status');
	const startDateParam = query?.get('startDate');
	const endDateParam = query?.get('endDate');
	const heniParam = query?.get(HENI_TAG_PARAM);

	// The HENI tag filters on the article's own flag, so it cannot be combined
	// with a category, artist or geotag. It wins over all three: a hand-edited or
	// stale url carrying both must still return a pure HENI feed.
	const isHeni =
		isQueryParamValid(heniParam) && heniParam === HENI_TAG_PARAM_VALUE;

	const isMetricValid =
		isQueryParamValid(metricParam) &&
		Object.values(METRICS).includes(metricParam as METRICS);

	const categories = [...Object.values(ArticleSearchCategory), 'all'];
	const isCategoryValid =
		isQueryParamValid(categoryParam) &&
		categories
			.map((category) => category.toLowerCase())
			.includes(
				(categoryParam as string).toLowerCase() as ArticleSearchCategory
			);

	const areMetricAndCategoryValid = (() => {
		if (!metricParam || !isMetricValid) {
			return isCategoryValid;
		}

		return areMetricAndCategoryConsistent(
			metricParam as METRICS,
			categoryParam as ArticleSearchCategory
		);
	})();

	const isDaterangeValid =
		isQueryParamValid(daterangeParam) &&
		['d', 'w', 'M', 'y', 'h'].includes((daterangeParam as string).slice(-1)) &&
		!isNaN(+(daterangeParam as string).slice(0, -1));

	const isStatusValid =
		isQueryParamValid(statusParam) &&
		Object.values(ArticleSearchStatus)
			.map((status) => status.toLowerCase())
			.includes((statusParam as string).toLowerCase() as ArticleSearchStatus);

	// isQueryParamValid checks that the query param is not null or an array of strings
	return {
		...(isQueryParamValid(phraseParam) && {
			phrase: phraseParam as string,
		}),
		...(isHeni && { heni: true }),
		...(!isHeni &&
			isQueryParamValid(artistParam) && {
				artist: artistParam as string,
			}),
		...(!isHeni &&
			isQueryParamValid(geotagParam) && {
				geotag: geotagParam as string,
			}),
		...(!isHeni &&
			isCategoryValid &&
			areMetricAndCategoryValid && {
				category: categoryParam as ArticleSearchCategory | 'all',
			}),
		...(isMetricValid &&
			areMetricAndCategoryValid && {
				metric: metricParam as METRICS,
			}),
		daterange: isDaterangeValid ? (daterangeParam as string) : 'all',
		...(isStatusValid && {
			status: statusParam as ArticleSearchStatus,
		}),
		...(isQueryParamValid(startDateParam) && {
			startDate: startDateParam as string,
		}),
		...(isQueryParamValid(endDateParam) && {
			endDate: endDateParam as string,
		}),
	};
};
